/**
 * DateChip component — shared types and Zod prop schema.
 *
 * A single calendar day chip: a bold day number, an uppercase weekday label, an
 * optional dot marker above the number, an optional "today" background pill, and
 * an optional Label badge. Two visual states (default transparent / selected
 * dark pill); `today` and the dot are orthogonal (a day can be both). Composed
 * horizontally by WeekStrip; renders identically in isolation (no upward coupling).
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { typography } from '../../elements/typography';
import { spacing } from '../../elements/spacing';
import { radius } from '../../elements/radius';
import { sizing } from '../../elements/sizing';
import { states } from '../../elements/states';
import { LABEL_RADIUS, LABEL_TYPOGRAPHY } from '../Label/Label';

export const DateChipState = z.enum(['default', 'selected']);
export type DateChipState = z.infer<typeof DateChipState>;

export const DateChipMarker = z.enum(['streak', 'dot', 'check']);
export type DateChipMarker = z.infer<typeof DateChipMarker>;

/** Intrinsic visual treatment for read-only status calendars. */
export const DateChipTone = z.enum(['default', 'success', 'highlight', 'outlined']);
export type DateChipTone = z.infer<typeof DateChipTone>;

/**
 * DateChip prop schema for the catalog (ADR-011).
 *
 * Data/variant props only. The press callback remains platform-specific; stable
 * automation and accessibility strings are catalog-safe caller-authored data.
 */
export const dateChipPropsSchema = z.object({
  dayLabel: z.string(),
  weekdayLabel: z.string(),
  state: DateChipState.default('default'),
  /** Marks the current day visually with a light background pill. Orthogonal to
   *  `marker` — a `today` chip may also carry a dot. Callers include its
   *  localized meaning in `accessibilityLabel` when that meaning is needed. */
  today: z.boolean().optional(),
  marker: DateChipMarker.optional(),
  tone: DateChipTone.optional(),
  /**
   * Announced meaning of the marker glyph (which is decorative). The caller
   * owns the copy (catalog-resolved per ADR-017) — the chip never hardcodes a
   * domain meaning for a generic marker. Absent means the marker stays silent.
   */
  markerAccessibilityLabel: z.string().optional(),
  /** Announced meaning of the `today` visual treatment — caller-owned copy. */
  todayAccessibilityLabel: z.string().optional(),
  /**
   * What activating this chip will do, announced after the label. The caller
   * owns the copy (catalog-resolved per ADR-017) — a chip in a week strip opens
   * a check-in, one in a booking picker does not, and the chip cannot know
   * which. Omit for a chip whose press outcome needs no explanation.
   */
  accessibilityHint: z.string().optional(),
  accessibilityLabel: z.string().optional(),
  badgeLabel: z.string().optional(),
  /** Exposes the chip as read-only status text rather than a selectable radio. */
  presentation: z.boolean().optional(),
});

export type DateChipProps = z.infer<typeof dateChipPropsSchema>;

/** Surface + text colour for a chip. Single source of truth. */
export interface DateChipColors {
  surface: string;
  text: string;
  borderColor: string;
  borderWidth: number;
}

/**
 * Text colour of a disabled chip on a transparent surface. The tertiary ink is
 * about 3.3:1 on the page background and 3.7:1 on a white container: lighter
 * than an active day, still above the 3:1 legibility floor.
 */
const DATE_CHIP_DISABLED_TEXT = colors.text.tertiary;

/**
 * Resolve the chip's surface + text colour. Selection wins: a selected chip is
 * always the dark `variationP` pill. Otherwise a `today` chip renders the light
 * `variationN` pill (its background is the "today" indicator). All other chips
 * are transparent. Text stays primary except on the inverted selected pill and
 * on a disabled chip over a transparent surface, whose text drops to the
 * tertiary ink so an unavailable day reads as greyed out (its accessible state
 * already says disabled). Selection and `today` outrank `disabled`, so a
 * recorded today stays the highlighted day. Tinted status tones keep primary
 * text: the tertiary ink is only measured against the page backgrounds.
 * Orthogonal to `marker` — a today chip can still carry a dot.
 */
export function dateChipColorsFor(
  state: DateChipState,
  today?: boolean,
  tone: DateChipTone = 'default',
  disabled = false,
): DateChipColors {
  if (state === 'selected') {
    return {
      surface: colors.misc.variationP,
      text: colors.text.inverted,
      borderColor: 'transparent',
      borderWidth: 0,
    };
  }
  if (today) {
    return {
      surface: colors.misc.variationN,
      text: colors.text.primary,
      borderColor: 'transparent',
      borderWidth: 0,
    };
  }
  if (tone === 'success') {
    return {
      surface: colors.background.success,
      text: colors.text.primary,
      borderColor: 'transparent',
      borderWidth: 0,
    };
  }
  if (tone === 'highlight') {
    return {
      surface: colors.background.highlight,
      text: colors.text.primary,
      borderColor: 'transparent',
      borderWidth: 0,
    };
  }
  const text = disabled ? DATE_CHIP_DISABLED_TEXT : colors.text.primary;
  if (tone === 'outlined') {
    return {
      surface: 'transparent',
      text,
      borderColor: colors.stroke.primary,
      borderWidth: states.borderWidth.default,
    };
  }
  return {
    surface: 'transparent',
    text,
    borderColor: 'transparent',
    borderWidth: 0,
  };
}

/**
 * Marker dot descriptor. `dot` and `streak` render the same small token-styled
 * dot above the number, coloured to the chip's text (inverted white on the dark
 * selected pill, primary on the light today pill / transparent chip). Returns
 * `null` when no marker is set; the row still reserves the dot's height so chips
 * stay uniform. Independent of `today` — a today chip may carry a dot.
 */
export interface DateChipMarkerDescriptor {
  color: string;
  glyph: 'dot' | 'check';
}

export function dateChipMarkerFor(
  marker: DateChipMarker | undefined,
  state: DateChipState,
  today?: boolean,
): DateChipMarkerDescriptor | null {
  if (!marker) return null;
  return {
    color: dateChipColorsFor(state, today).text,
    glyph: marker === 'check' ? 'check' : 'dot',
  };
}

/**
 * Compose a screen-reader label from visible copy plus caller-authored marker
 * meaning. Selected state is exposed structurally by the radio semantics and
 * `today` is purely visual unless the caller includes it in accessibilityLabel.
 */
export function dateChipAccessibleLabelFor(input: {
  weekdayLabel: string;
  dayLabel: string;
  state: DateChipState;
  today?: boolean;
  marker?: DateChipMarker;
  markerAccessibilityLabel?: string;
  todayAccessibilityLabel?: string;
  badgeLabel?: string;
}): string {
  const parts: string[] = [`${input.weekdayLabel} ${input.dayLabel}`];
  if (input.today && input.todayAccessibilityLabel) {
    parts.push(input.todayAccessibilityLabel);
  }
  // The glyph is decorative; its meaning is announced only when the caller
  // provides localized copy (ADR-017).
  if (input.marker && input.markerAccessibilityLabel) {
    parts.push(input.markerAccessibilityLabel);
  }
  if (input.badgeLabel) parts.push(input.badgeLabel);
  return parts.join(', ');
}

/** Typography. */
export const DATE_CHIP_NUMBER_TYPOGRAPHY = typography.b1Bold;
export const DATE_CHIP_WEEKDAY_TYPOGRAPHY = typography.c2Regular;

/**
 * Chip metrics. A plain chip is 48 wide; the selected chip is deliberately a
 * size step larger than a plain one (wider side padding, an even vertical
 * inset), so selection changes size as well as colour. A badged chip is as
 * wide as a selected chip and no taller than a plain one: its badge is the
 * compact one below, sitting directly under the number.
 */
export const DATE_CHIP_RADIUS = radius.xl;
export const DATE_CHIP_PADDING_TOP = spacing.s;
export const DATE_CHIP_PADDING_BOTTOM = spacing.sm;
export const DATE_CHIP_PADDING_HORIZONTAL = spacing.s;
export const DATE_CHIP_SELECTED_PADDING_HORIZONTAL = spacing.sm;
export const DATE_CHIP_SELECTED_PADDING_VERTICAL = spacing.s;
export const DATE_CHIP_CONTENT_WIDTH = spacing.xl;
/** Gap between the day number and the weekday beneath it. */
export const DATE_CHIP_CONTENT_GAP = spacing.xxs;
/**
 * Gap between the marker slot and the day number: none, so the number sits
 * directly under the marker. Zero spacing is the literal `0`, matching the
 * design system's convention: the `spacing` token scale deliberately starts at
 * `xxs` (2) and excludes 0, as Stack maps its `'none'` option to `0`.
 */
export const DATE_CHIP_MARKER_GAP = 0;

/**
 * Badged chip. The calendar day component draws its badge as a compact label:
 * one caption line tall with no vertical padding, sitting directly under the
 * number. The chip is the selected chip's width (content column plus the
 * selected side padding) and narrows its own side padding so a short badge
 * word fits centred inside that width. The badge reuses the Label's caption
 * typography and corner radius, so it still reads as a Label.
 */
export const DATE_CHIP_BADGE_PADDING_HORIZONTAL = spacing.xs;
export const DATE_CHIP_BADGE_MIN_WIDTH =
  DATE_CHIP_CONTENT_WIDTH + DATE_CHIP_SELECTED_PADDING_HORIZONTAL * 2;
export const DATE_CHIP_BADGE_TYPOGRAPHY = LABEL_TYPOGRAPHY;
export const DATE_CHIP_BADGE_HEIGHT = LABEL_TYPOGRAPHY.lineHeight;
export const DATE_CHIP_BADGE_RADIUS = LABEL_RADIUS;
export const DATE_CHIP_BADGE_INSET_HORIZONTAL = spacing.xs;
/** Gap between the day number and the badge: none (see DATE_CHIP_MARKER_GAP). */
export const DATE_CHIP_BADGE_GAP = 0;

/** Resolve a chip's padding: selection sets an even inset, a badge narrows the sides. */
export function dateChipPaddingFor(
  state: DateChipState,
  badged: boolean,
): { top: number; bottom: number; horizontal: number } {
  if (state === 'selected') {
    return {
      top: DATE_CHIP_SELECTED_PADDING_VERTICAL,
      bottom: DATE_CHIP_SELECTED_PADDING_VERTICAL,
      horizontal: DATE_CHIP_SELECTED_PADDING_HORIZONTAL,
    };
  }
  return {
    top: DATE_CHIP_PADDING_TOP,
    bottom: DATE_CHIP_PADDING_BOTTOM,
    horizontal: badged ? DATE_CHIP_BADGE_PADDING_HORIZONTAL : DATE_CHIP_PADDING_HORIZONTAL,
  };
}

/** Minimum chip width: the touch target, widened to the badged chip's width. */
export function dateChipMinWidthFor(badged: boolean): number {
  return badged ? Math.max(DATE_CHIP_MIN_TARGET, DATE_CHIP_BADGE_MIN_WIDTH) : DATE_CHIP_MIN_TARGET;
}

/** Gap between the number and the line under it (weekday, or the compact badge). */
export function dateChipLabelsGapFor(badged: boolean): number {
  return badged ? DATE_CHIP_BADGE_GAP : DATE_CHIP_CONTENT_GAP;
}

/** Marker metrics. */
export const DATE_CHIP_MARKER_SIZE = spacing.s;
export const DATE_CHIP_MARKER_RADIUS = radius.rounded;

/** Minimum interactive target per ADR-025 — the shared design-system token
 *  (expanded further via hitSlop/padding). */
export const DATE_CHIP_MIN_TARGET = sizing.touchTarget.minimum;
